import axios from '@axios'
import config from '@core/app-config/api-config'

class RaceAnalysisApiService {
  constructor() {
    this.baseUrl = config.host
  }

  // Get editable state from server
  async getState(startId, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/state`
      : `${this.baseUrl}/race/manipulation/${startId}/state`
    const response = await axios.get(url)
    return response.data
  }

  // Initial analysis
  async analyze(startId, raceConfig, overrideList, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/analyze/${startId}/${distanceId}`
      : `${this.baseUrl}/race/analyze/${startId}`
    const response = await axios.post(url, {
      config: raceConfig,
      overrideList: overrideList || [],
    })
    return response.data
  }

  // Batch apply all manipulations at once (RECOMMENDED for multiple changes)
  async applyBatchManipulations(startId, manipulations, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/apply-batch`
      : `${this.baseUrl}/race/manipulation/${startId}/apply-batch`
    const response = await axios.post(url, {
      manipulations,
    })
    return response.data
  }

  // Individual manipulation methods
  async addVirtualReading(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/add-virtual-reading`
      : `${this.baseUrl}/race/manipulation/${startId}/add-virtual-reading`
    const response = await axios.post(url, data)
    return response.data
  }

  async reassignReading(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/reassign-reading`
      : `${this.baseUrl}/race/manipulation/${startId}/reassign-reading`
    const response = await axios.post(url, data)
    return response.data
  }

  async setCheckpointTime(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/set-checkpoint-time`
      : `${this.baseUrl}/race/manipulation/${startId}/set-checkpoint-time`
    const response = await axios.post(url, data)
    return response.data
  }

  async removeCheckpointOverride(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/remove-checkpoint-override`
      : `${this.baseUrl}/race/manipulation/${startId}/remove-checkpoint-override`
    const response = await axios.post(url, data)
    return response.data
  }

  async setStatus(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/set-status`
      : `${this.baseUrl}/race/manipulation/${startId}/set-status`
    const response = await axios.post(url, data)
    return response.data
  }

  async removeVirtualReading(startId, readingKey, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/virtual-reading/${readingKey}`
      : `${this.baseUrl}/race/manipulation/${startId}/virtual-reading/${readingKey}`
    const response = await axios.delete(url)
    return response.data
  }

  async updateTimeConstraints(startId, data, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/time-constraints`
      : `${this.baseUrl}/race/manipulation/${startId}/time-constraints`
    const response = await axios.post(url, data)
    return response.data
  }

  async reprocess(startId, distanceId = null) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/reprocess`
      : `${this.baseUrl}/race/manipulation/${startId}/reprocess`
    const response = await axios.post(url)
    return response.data
  }

  async exportCsv(startId, distanceId = null) {
    const params = {}
    if (distanceId !== null && distanceId !== undefined) {
      params.distanceId = distanceId
    }

    const response = await axios.get(`${this.baseUrl}/race/analyze-results/${startId}/export`, {
      params,
      responseType: 'blob',
    })
    return response
  }

  async getConfig(startId) {
    const response = await axios.get(`${this.baseUrl}/race/config/${startId}`)
    return response.data
  }

  async getConfigByDistance(startId, distanceId) {
    const response = await axios.get(`${this.baseUrl}/race/config/${startId}/${distanceId}`)
    return response.data
  }

  /**
   * Paginated saved results for a distance (DB read). Use forAdmin=1 for admin grid shape.
   */
  async getResultsPageByDistance(startId, distanceId, query = {}) {
    const params = new URLSearchParams()
    if (query.page != null) params.set('page', String(query.page))
    if (query.maxResultCount != null) params.set('maxResultCount', String(query.maxResultCount))
    if (query.filterText) params.set('filterText', query.filterText)
    if (query.gender) params.set('gender', query.gender)
    if (query.group) params.set('group', query.group)
    params.set('violationOnly', query.violationOnly ? '1' : '0')
    params.set('forAdmin', '1')
    const qs = params.toString()
    const url = `${this.baseUrl}/race/analyze-results/${startId}/${distanceId}?${qs}`
    const response = await axios.get(url)
    return response.data
  }

  async getRaceAnalysisFiltersByDistance(startId, distanceId) {
    const response = await axios.get(
      `${this.baseUrl}/race/analyze-results/${startId}/distances/${distanceId}/filters`,
    )
    return response.data
  }

  async getDistancesByStartId(startId) {
    const response = await axios.get(`${this.baseUrl}/start/${startId}/distances`)
    return response.data
  }

  async saveConfig(startId, distanceId, config, clientSessionId = null) {
    const url = `${this.baseUrl}/race/config/${startId}/${distanceId}/save`
    const response = await axios.post(url, { config, clientSessionId })
    return response.data
  }

  async massDns(startId, distanceId = null, preview = false) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/mass-dns?preview=${preview}`
      : `${this.baseUrl}/race/manipulation/${startId}/mass-dns?preview=${preview}`
    const response = await axios.post(url)
    return response.data
  }

  async massDnf(startId, distanceId = null, preview = false) {
    const url = distanceId
      ? `${this.baseUrl}/race/manipulation/${startId}/${distanceId}/mass-dnf?preview=${preview}`
      : `${this.baseUrl}/race/manipulation/${startId}/mass-dnf?preview=${preview}`
    const response = await axios.post(url)
    return response.data
  }

  // External race API mock
  async getMockSettings() {
    const response = await axios.get(`${this.baseUrl}/race/mock/settings`)
    return response.data
  }

  async setMockSettings(useMock) {
    const response = await axios.put(`${this.baseUrl}/race/mock/settings`, { useMock })
    return response.data
  }

  async getMockList() {
    const response = await axios.get(`${this.baseUrl}/race/mock`)
    return response.data
  }

  async getMock(startId) {
    const response = await axios.get(`${this.baseUrl}/race/mock/${startId}`)
    return response.data
  }

  async putMock(startId, data) {
    const response = await axios.put(`${this.baseUrl}/race/mock/${startId}`, { data })
    return response.data
  }

  async deleteMock(startId) {
    await axios.delete(`${this.baseUrl}/race/mock/${startId}`)
  }

  // Task 10: race lifecycle (per-distance finalize / unfinalize)
  async getLifecycleState(startId, distanceId) {
    const response = await axios.get(`${this.baseUrl}/race/lifecycle/${startId}/${distanceId}`)
    return response.data
  }

  async finalizeDistance(startId, distanceId, reason) {
    const response = await axios.post(
      `${this.baseUrl}/race/lifecycle/${startId}/${distanceId}/finalize`,
      reason ? { reason } : {},
    )
    return response.data
  }

  async unfinalizeDistance(startId, distanceId, reason) {
    const response = await axios.post(
      `${this.baseUrl}/race/lifecycle/${startId}/${distanceId}/unfinalize`,
      { reason },
    )
    return response.data
  }

  async dryRunResultsImport(startId, distanceId, { file, mode, idempotencyKey }) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mode', mode)
    formData.append('idempotencyKey', idempotencyKey)
    const response = await axios.post(
      `${this.baseUrl}/race/import-results/${startId}/${distanceId}/dry-run`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  }

  async applyResultsImport(startId, distanceId, { importRunId, idempotencyKey }) {
    const response = await axios.post(
      `${this.baseUrl}/race/import-results/${startId}/${distanceId}/apply`,
      { importRunId, idempotencyKey },
    )
    return response.data
  }

  async downloadResultsImportTemplate(startId, distanceId) {
    const response = await axios.get(
      `${this.baseUrl}/race/import-results/${startId}/${distanceId}/template`,
      { responseType: 'blob' },
    )
    return response
  }
}

export default new RaceAnalysisApiService()
